// AppShellHost — the composition root and the ONE impure shell.
//
// This is the only stateful/impure file in the mock app: it holds the frame
// state, calls the `AppPorts` seam inside `useEffect` (the sanctioned impurity
// boundary — fetch-shaped calls + `useState`, not a buried `void` smell), and
// folds every reported intent through the pure mock folds. Every component it
// renders stays pure and receives its data via props.
//
// Frame state lives in ONE `FrameState` product held by a single
// `useReducer(foldFrame, …)` — the pure frame decider (app/frame.ts) is the
// ONLY writer of frame factors (composition contract I2): handlers dispatch
// named `FrameTransition` values instead of setting factors imperatively, so
// a transition that moves an unnamed region cannot exist. The factors:
//   openSecondary — which secondary is summoned, or null for Focus (closed)
//   nav      — the rail's display state
//   paneTree — the author's working memory (TabSet, whole-workspace now that
//              Project no longer sits between workspace and resource — the
//              active tab routes the centre)
//   overlay — the one transient surface floated over everything (Overlay)
//   messages — the toasts, last-step line and note at an object showing
// plus anchor, the closed-tab + recency memories and the
// blocked-close confirmation. Outside the frame: the per-material compose
// wear (loop, folded via composeLoop.ts), the loaded data (the workspace
// library / elements / conversation / the active source or package view
// detail) and a drafting flag.
//
// Object-nav wiring notes (deliberate MVP shortcuts, each commented in place):
//   • Tabs are never dirty here — every edit commits transactionally through
//     the update primitives (optimistic + rollback), so there is no unsaved
//     buffer to guard. The dirty/discard machinery stays wired (the folds and
//     ConfirmDiscard render when a dirty tab exists) for the day a buffered
//     editor arrives.
//   • The chat context chip is PORT-HONEST: `ports.conversationScope` decides
//     the displayed scope and the switchable set, so the chip never claims a
//     projection the backend doesn't serve (the real server's conversations
//     are workspace-global; the mock's are per-material).

import * as Core from "@bilda/core";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useEffect, useReducer, useRef, useState } from "react";
import type {
    SecondarySlot,
    SlotAvailability,
} from "../../components/AppShell/AppShell.js";
import type { ChatScope } from "../../components/Chat/presentation.js";
import { useCoachHost } from "../../components/CoachHost/CoachHost.js";
import { useEditorHost } from "../../components/EditorHost/EditorHost.js";
import type { SurfaceSlash } from "../../components/EditorHost/types.js";
import { tourViewOf } from "../../components/GuidedTour/viewTours.js";
import type { IngestHostHandle } from "../../components/IngestHost/IngestHost.js";
import { useNavHost } from "../../components/NavHost/NavHost.js";
import { dockedConversationOf } from "../../components/Navigation/chatDock.js";
import {
    isSingleLeaf,
    leavesOf,
    tabSetOfPaneTree,
} from "../../components/Navigation/paneTree.js";
import type { ResourceRef, TabSet } from "../../components/Navigation/types.js";
import { partsBefore } from "../../components/StudioEditor/studioCreate.js";
import {
    studioSlashItems,
    studioSlashKeyAt,
} from "../../components/StudioEditor/studioSlash.js";
import {
    type ChatSession,
    chatSessionFor,
    visibleConversationIds as conversationIdsInLeaves,
} from "../../modules/chat/model.js";
import type { TurnFailure } from "../../modules/chat.js";
import { anchorForActiveTab } from "../contextAnchor.js";
import { loadableConversationId } from "../conversationSelection.js";
import { readDockPreference, writeDockPreference } from "../dockPreference.js";
import { type ExternalError, toDisplay } from "../externalError.js";
import {
    chatPlaceOf,
    dockState,
    foldFrame,
    type HostNotice,
    initialFrame,
    type OverlaySurface,
    workspaceSurface,
} from "../frame.js";
import type { IngestFile, IngestId } from "../ingest.js";
import { resolveKbdPlatform } from "../kbdPlatform.js";
import type { QueueControl } from "../messageQueue.js";
import { panelDragSettle } from "../panelDragOpen.js";
import { clampRail, writePanelLayout } from "../panelLayout.js";
import type {
    AuthStatus,
    Library,
    LoadedExport,
    LoadedSource,
} from "../ports.js";
import { useAppPorts } from "../ports.js";
import { readStartingRail, writeRailPreference } from "../railPreference.js";
import { readViewState } from "../viewState.js";
import { studioOpenOf } from "./Centre/studioOpen.js";
import { AMBIENT_CONVERSATION_ID, TIP_ROTATION_MS } from "./constants.js";
import { editedMaterialRef } from "./editedMaterial.js";
import { deriveLibraryRefs } from "./libraryRefs.js";
import type { MessageControls } from "./MessageLayer.js";
import { useAuthoringDraftState } from "./useAuthoringDraftState.js";
import { useChatModule } from "./useChatModule.js";
import { useChatSlashCommands } from "./useChatSlashCommands.js";
import { useConversationList } from "./useConversationList.js";
import { useMaterialReviewState } from "./useMaterialReviewState.js";
import { usePanelLayout } from "./usePanelLayout.js";
import { useShellRouting } from "./useShellRouting.js";
import { useStudioCommands } from "./useStudioCommands.js";
import { useStudioPlace } from "./useStudioPlace.js";
import { useTransientSurfaceState } from "./useTransientSurfaceState.js";
import { useWorkspaceSettings } from "./useWorkspaceSettings.js";
import "../../components/PaneView/PaneView.css";

// useShellFoundation — the shell's state + derivations + the early host hooks
// (nav / coach / editor / the chat module's mount) + the load-adjacent
// handlers, split out of useAppShellHost so each file stays under the size
// gate. Returns the foundation bundle the orchestrator wires the remaining
// sub-hooks + model against.
export const useShellFoundation = () => {
    const ports = useAppPorts();
    const { t, i18n } = useLingui();
    // Localize a typed failure into the one-line string the toast/dialog
    // slots consume: the display's title, with the body appended when the
    // elaboration earns the space. Views that render a richer surface
    // (ErrorBanner) read the full Display themselves.
    const displayText = (e: ExternalError): string => {
        const d = toDisplay(e);
        return d.body === undefined ? t(d.title) : `${t(d.title)} ${t(d.body)}`;
    };
    // A chat turn's failure, localized (the composer's error line + toast).
    const turnFailureText = (failure: TurnFailure): string =>
        failure.type === "port_refused"
            ? displayText(failure.error)
            : t`Couldn't start the conversation. Please try again.`;
    // Which platform register shortcut hints render in — a boundary read
    // (a minimal navigator sniff), constant for the session; the pure
    // components take it as a prop.
    const kbdPlatform = resolveKbdPlatform();

    // THE frame: every frame factor in one product, advanced ONLY by the pure
    // frame decider (app/frame.ts — composition contract I2). Seeded from the
    // device-persisted view state (U46, master audit P12): the open tabs
    // survive a full reload, written back by the persist effect below.
    // Restored ids are trusted only as far as SHAPE — existence is
    // re-established once the world loads (`reconcile-tabs`). The closed-tab
    // and recency memories are deliberately session-local (only the OPEN
    // working set persists — U46).
    const [frame, dispatch] = useReducer(foldFrame, undefined, () =>
        initialFrame(readViewState(), readDockPreference(), readStartingRail()),
    );
    // The 404 gate (F48): raised by the URL seam (useUrlNavigation, wired below
    // once refFor/durability/tabs exist) when the path is unrecognized, read by
    // AppShellHost to render NotFoundGate. A projection of the URL boundary held
    // in the shell (like authStatus), not a frame factor — a 404 is a URL-layer
    // fact, not a workspace tab.
    const [routeNotFound, setRouteNotFound] = useState(false);
    const { overlay, openSecondary, nav, anchor, paneTree } = frame;

    // Persist the dock's open/closed preference (J19): every change to
    // `openSecondary` writes through to the device — the same sanctioned
    // localStorage effect as the theme + the working set. The dock rests OPEN by
    // default (dockPreference.ts seeds the reducer above); a deliberate close is
    // remembered so the dock stays closed across reloads rather than re-opening.
    // Device UX preference, never a domain event.
    useEffect(() => {
        writeDockPreference(openSecondary);
    }, [openSecondary]);

    // Persist the LEFT rail's expand/collapse preference (J35): every CHANGE
    // to `nav` writes through to the device — the same sanctioned localStorage
    // effect as the dock above. The rail rests EXPANDED by default
    // (railPreference.ts seeds the reducer above); a deliberate collapse is
    // remembered so the rail stays collapsed across reloads rather than
    // re-expanding. The seed itself is not written back: a narrow window starts
    // collapsed (readStartingRail) without overwriting the wide-window
    // preference. A transient "hidden" nav (narrow viewport) is left
    // un-persisted. Device UX preference, never a domain event.
    const persistedNav = useRef(nav);
    useEffect(() => {
        if (nav === persistedNav.current) return;
        persistedNav.current = nav;
        writeRailPreference(nav);
    }, [nav]);

    // Transient view-surface state — the create-dialog seeds + dev preset/
    // promote flags (prefetched on palette-open via the effect this hook owns),
    // the History & Inspection overlay data, the finder corpus, and the inline
    // rename/delete-confirm transients. Parked at the foundation (declared +
    // returned, never read here); spread into the bundle below. Called at the
    // cells' original position so the hook-call order is unchanged.
    const transientSurfaces = useTransientSurfaceState();

    // The rail + dock widths + drag-to-expand pulls + the dock release handler
    // — the pure `usePanelLayout` seam. The rail's release handler stays here
    // (below) because it also reveals the rail (a navHost intent).
    const {
        panelLayout,
        setPanelLayout,
        resizeRail,
        resizeDock,
        resetRail,
        resetDock,
        dockDragPull,
        setDockDragPull,
        railDragPull,
        setRailDragPull,
        dockDragCfg,
        railDragCfg,
        endDockDrag,
    } = usePanelLayout(dispatch);
    const endRailDrag = (pullPx: number) => {
        setRailDragPull(null);
        const settle = panelDragSettle(pullPx, railDragCfg);
        if (settle.type !== "open") return;
        setPanelLayout((l) => {
            const next = { ...l, railWidth: clampRail(settle.widthPx) };
            writePanelLayout(next);
            return next;
        });
        revealRail();
    };

    // Loaded data (null ⇒ not yet loaded). Load failures keep the TYPED
    // error (never a boolean): the pane renders `toDisplay`'s honest
    // message + recovery instead of a generic "couldn't load" line.
    const [library, setLibrary] = useState<Library | null>(null);
    const [libraryError, setLibraryError] = useState<ExternalError | null>(
        null,
    );

    // The Settings→Account cells (display name, summary consent, workspace
    // name) — `useWorkspaceSettings`. `showNotice` is declared further down,
    // so it is reached through a closure (called only from async handlers).
    const {
        displayName,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange,
    } = useWorkspaceSettings({
        ports,
        library,
        displayText,
        showNotice: (n) => showNotice(n),
    });
    // The projected auth status (postgres+auth mode). `null` while the boot
    // probe is in flight; then one of the three `AuthStatus` arms. An
    // `anonymous` projection is the SIGN-IN GATE — the render short-circuits to
    // the sign-in surface (see below) instead of hammering `/state` and showing
    // a raw 401. `unavailable` (fs-mode, no auth surface) and `authenticated`
    // both fall through to the app. This is auth-as-projected-state: the gate is
    // a deterministic read of `authStatus`, never a buried redirect.
    const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
    // The last replay-degradation signature the calm boot notice was raised
    // for. Guards against re-toasting on every retry/heartbeat re-read
    // (alarm-discipline): the notice fires once per CHANGED report — on the
    // initial degraded boot and again only if a later re-fold (e.g. loading a
    // different log) produces a different degradation. A clean boot ("0:0")
    // silently resets it. Boot-health is a per-fold fact, so a signature is
    // all the memory the once-guard needs.
    const bootHealthSigRef = useRef<string>("0:0");
    // Monotonic seed for shell-minted pane ids (split views); paired with a
    // timestamp so a fresh session never collides with persisted ids.
    const paneIdSeqRef = useRef(0);
    // Same idea for the open material: keep its content on a same-material
    // re-read (a backoff bump while offline) so the open material survives a
    // mid-session disconnect; clear only on a genuine material switch.
    const loadedMaterialRef = useRef<string | null>(null);
    // The active source/package tab's view detail (null until loaded; reset on
    // every active-resource change).
    const [sourceDetail, setSourceDetail] = useState<LoadedSource | null>(null);
    const [sourceError, setSourceError] = useState<ExternalError | null>(null);
    // Bumped after a summary/note command so the source view re-reads.
    const [sourceReloadKey, setSourceReloadKey] = useState(0);
    const [exportDetail, setExportDetail] = useState<LoadedExport | null>(null);
    const [exportError, setExportError] = useState<ExternalError | null>(null);
    // The chat module, mounted in its runtime (S6): its Model is the client-side
    // chat turn flow's ONE state cell. Every chat mutation is a command sent
    // into it (`sendChat`); `chatSessions`/`selectedConversationId` are
    // read-through projections for the many render sites.
    const { chatState, sendChat, chatRuntime } = useChatModule(ports);
    const chatSessions = chatState.sessions;
    // The selected aggregate survives navigation changes (ADR-6: absorbed into
    // ChatState — the active tab only decides whether the conversation owns the
    // centre; the selection persists even while another tab is focused). The
    // decider's `conversation_selected` command is the sole writer.
    const selectedConversationId = chatState.selectedConversationId;
    const sessionFor = (conversationId: string | null): ChatSession =>
        chatSessionFor(chatSessions, conversationId);
    // The rail's conversation list (a remote read) + the durability /
    // emptiness projections derived off it — `useConversationList`.
    const {
        conversationList,
        setConversationList,
        conversationListSeq,
        retryConversationList,
        conversationSummaries,
        updateConversationSummaries,
        noteConversationStarted,
        conversationIsDurable,
        conversationIsEmpty,
        conversationTranscriptPending,
    } = useConversationList(sessionFor);
    // The grounding / quality / policy / pane-subject cells + the off-screen
    // landed-proposal parking ref — `useMaterialReviewState`.
    const {
        learningContexts,
        setLearningContexts,
        quality,
        setQuality,
        policy,
        setPolicy,
        policyCache,
        setPolicyCache,
        paneSubject,
        setPaneSubject,
        pendingProposalsByMaterialRef,
    } = useMaterialReviewState();

    // UI-local state.
    // The landing's rotating capability tip and its opt-out — device-local UI
    // state, not a domain fact (like the theme). The index cycles a small
    // static set; `tipsDismissed` hides the aside entirely.
    const [tipIndex, setTipIndex] = useState(0);
    const [tipsDismissed, setTipsDismissed] = useState(false);
    // Authoring/draft/export surface state — the active tab's resolved
    // explicit + ambient context items, the export flow's machine + finished-
    // package handle, the active material's template identity, and the create
    // dialog's catalogue + submission progress. Park-only at the foundation
    // (declared + returned, never read here); spread into the bundle below.
    // Called at the cells' original position so the hook-call order is unchanged.
    const authoringDraft = useAuthoringDraftState();

    // Source-ingest (owner decision B5): the picker, the window drag-drop
    // listeners and the batch progress fold now live in IngestHost
    // (strangler-fig P1 — .claude/analysis/ui-ddd-decomposition.md §6 Phase 1).
    // `ingest` is a read-only MIRROR the host reports via `onIngestChanged`,
    // kept here only because the rail's quiet "Adding N sources…" status
    // line (NavRail, not yet extracted) still reads `pendingCount(ingest)`.
    // `openFilePicker` reaches the hidden `<input>` IngestHost owns through
    // its imperative handle — every "add source" affordance (rail "+", File
    // menu, WorkspaceHome, desktop accelerators) keeps calling this same name.
    const [ingest, setIngest] = useState<readonly IngestFile[]>([]);
    // F83 linkage: which conversation was active when each file was queued, so
    // the pure `composerAttachmentsFor` projection knows whose composer the
    // optimistic chip belongs to. Recorded at drop/pick time (IngestHost's
    // `onFilesQueued`); a drop with no active conversation records nothing (the
    // rail-only path). Session-lived + small (one ordinal per queued file); a
    // VIEW-TRANSIENT map, never persisted or an event payload.
    const [ingestConversations, setIngestConversations] = useState<
        ReadonlyMap<IngestId, string>
    >(new Map());
    const ingestHostRef = useRef<IngestHostHandle>(null);
    const openFilePicker = () => ingestHostRef.current?.open();
    // A paste too long for the block it landed in comes in as a source,
    // through the same door as a picked file (ui-brief, hostile case).
    const addPastedText = (text: string) =>
        ingestHostRef.current?.add([
            new File([text], `${t`Pasted text`}.txt`, { type: "text/plain" }),
        ]);

    // === Derivations off the library + tabs ==================================

    // Library-derived shared vocabulary (rail lists, tab-id search space,
    // @-mention pool, ambient-DTO resolver) — the pure `deriveLibraryRefs`.
    const {
        materialRefs,
        sourceItems,
        packageItems,
        refFor,
        resolveAmbientItems,
        composerCandidates,
    } = deriveLibraryRefs(library, t`Untitled`);

    // The composer's `/`-slash quick actions (pure hook).
    const chatSlashCommands = useChatSlashCommands();

    // The activity rail's own state (which object-kind category panel shows) —
    // moved out of this file's body into NavHost.tsx (host extraction,
    // strangler-fig pattern). The Overview panel it once also fed was retired
    // (owner-settled 2026-08-19): the landing is the pinned Home tab now.
    const navHost = useNavHost({
        sources: sourceItems,
        materials: materialRefs,
        packages: packageItems,
    });

    // Expand the rail zone AND ensure a panel is showing. The rail's width is
    // navState-driven (frame), but WHICH panel shows is activeCategory-driven
    // (navHost) — so a bare `expand-rail` (drag / click the collapsed inner
    // edge, narrow re-summon) with no category active would widen to an EMPTY
    // 240px zone. Default to Materials (the core browse surface) only when
    // nothing is open; a retained category (keyboard collapse kept it)
    // re-reveals as-is.
    const revealRail = () => {
        if (navHost.activeCategory === null)
            navHost.selectCategory("materials");
        dispatch({ type: "expand-rail" });
    };

    // The working set, derived from the workspace's (single-leaf) pane tree —
    // a VIEW over the stored tree (P-CHROME step 2); `isSplit` switches the
    // render to the recursive PaneView.
    const isSplit = !isSingleLeaf(paneTree);
    const tabs: TabSet = tabSetOfPaneTree(paneTree);
    const activeRef: ResourceRef | null =
        tabs.active.type === "resource"
            ? (refFor(tabs.active.id) ?? null)
            : null;
    // Where the author is in the Studio: its edited document is the one the
    // editor works on while the Studio is shown.
    const place = useStudioPlace(
        library,
        frame.studioSurface,
        frame.studioProject,
    );
    const activeMaterialRef = editedMaterialRef(
        tabs.active,
        refFor,
        place.edited,
    );
    const activeMaterialId: string | null = activeMaterialRef?.id ?? null;
    // The conversation the docked chat panel shows (chatDock.ts) — the one the
    // composer sends to, whatever the main view shows.
    const activeConversationId: string = dockedConversationOf(paneTree);

    // === URL navigation (F48) ================================================
    // The new-chat transitions, the 404 gate's way out and the pushState/
    // popstate seam — `useShellRouting`.
    const { onRouteHome } = useShellRouting({
        frame,
        dispatch,
        refFor,
        notFound: routeNotFound,
        setNotFound: setRouteNotFound,
        tabs,
        conversationList,
        conversationIsDurable,
    });
    // Only DURABLE conversations are loadable (hero-is-initial-conversation-state
    // §5.1): a nascent conversation has no server aggregate, so attempting a
    // transcript load would error. Gating here means the load effect never fires
    // for a nascent tab — and because the load happened (once) while the
    // conversation was still empty, the first send does NOT trigger a fresh load,
    // so there is no tab-kind-swap instant for a load to race against. When the
    // first send makes the conversation durable, this key changes and the load
    // effect re-runs; the P1.4 merge guards that in-flight load.
    const loadableDurableConversationId = (
        conversationId: string,
    ): string | null => {
        const base = loadableConversationId(conversationId);
        return base !== null && conversationIsDurable(base) ? base : null;
    };
    // The panes' conversations plus the one docked in the chat panel.
    const visibleConversationIds = conversationIdsInLeaves(
        [
            ...leavesOf(paneTree.root),
            { active: { type: "conversation", id: activeConversationId } },
        ],
        loadableDurableConversationId,
    );
    const visibleConversationIdsKey = [...visibleConversationIds]
        .sort()
        .join("\u0000");
    const selectedSession = sessionFor(selectedConversationId);
    // The one field the shell reads off the selected session directly: an
    // in-flight turn arms the Escape-to-stop listener (else it reads per-convo).
    const { drafting } = selectedSession;

    // biome-ignore lint/correctness/useExhaustiveDependencies: activeConversationId IS the selection signal; sendChat dispatches into the runtime's latest Model.
    useEffect(() => {
        if (activeConversationId !== AMBIENT_CONVERSATION_ID) {
            sendChat({
                type: "conversation_selected",
                conversationId: activeConversationId,
            });
        }
    }, [activeConversationId]);
    const activeSourceId: string | null =
        activeRef?.type === "source" ? activeRef.id : null;
    const activeExportId: string | null =
        activeRef?.type === "export" ? activeRef.id : null;
    // The material a full-quality report TAB reports on, when such a tab is
    // active — the id the report body sources its evaluation by (via
    // `policyCache`). Null on any other active tab.
    const activeQualityMaterialId: string | null =
        tabs.active.type === "quality" ? tabs.active.materialId : null;
    // The active tab's Context anchor (context spine step 3a) — the SAME
    // key the always-visible strip, the ContextSideCard's Grounding section,
    // the composer's `@`-mention dispatch, and `sendTurn`'s explicit-context
    // block all target. Derived straight off the active TAB (not `activeRef`),
    // per `contextAnchor.ts`: a resource tab keys by its resource id, a
    // conversation tab by its conversation id, and Settings/Hero/Quality read
    // `null` (no honest anchor yet — see that module's header note). The chat
    // is docked beside the main view (chatDock.ts), so its conversation is the
    // anchor (there is always one) — the composer sends from the panel.
    const activeContextAnchor: Core.EntityRef.Ref | null = anchorForActiveTab({
        type: "conversation",
        id: activeConversationId,
    });
    // A stable string re-run signal for effects/memos keyed on the anchor —
    // `activeContextAnchor` itself is a fresh object every render.
    const activeContextAnchorKey: string | null =
        activeContextAnchor === null
            ? null
            : Core.EntityRef.entityRefKey(activeContextAnchor);
    // Where the chat sits on this view (#437, frame/derive.ts `chatPlaceOf`),
    // and the right dock as ONE derived fact gating ALL the dock's parts in
    // AppShell so body + handle can't disagree (ui-robustness.md).
    const chatPlace = chatPlaceOf(tabs.active, frame.studioSurface);
    const dock = dockState(chatPlace, openSecondary);

    // Whether the docked chat shows an EMPTY conversation — the hero composer is
    // on show (hero-is-initial-conversation-state §1.1). Derived from the
    // session's transcript length, never stored. Drives the composer autofocus
    // and the tip rotation.
    const activeConversationEmpty: boolean =
        conversationIsEmpty(activeConversationId);
    // The centre's surface register (landing | paper) — the ambient-vs-paper
    // background swap. An EMPTY conversation (the hero composer) is the landing;
    // any other focused tab — including a conversation with a transcript — lifts
    // the centre to paper.
    const surface = workspaceSurface(tabs.active, activeConversationEmpty);

    // Autofocus-on-arrival: whenever the landing becomes the centre (any entry
    // path), focus its composer so the author can just start typing. The one
    // sanctioned effect, at the impure host edge — the pure landing tree never
    // reaches for the DOM. A no-op on the first-run Welcome (no composer, ref
    // stays null). Re-runs when the library first resolves too: on a COLD load
    // the composer subtree settles as the async read lands, so a mount-only
    // focus would target a node the settle then replaces — `libraryReady`
    // transitions exactly once (null→loaded), re-landing focus on the final
    // node without stealing it on later reloads.
    const heroActive = activeConversationEmpty;
    const libraryReady = library !== null;
    // The composer is the shared PowerComposer at `hero` size (no `inputRef`
    // seam) — so the host focuses it by querying its stable landing class, a
    // sanctioned host-edge DOM read. `libraryReady` is a deliberate re-run trigger (a cold load
    // replaces the composer subtree as the async read lands), not read in-body.
    // biome-ignore lint/correctness/useExhaustiveDependencies: libraryReady transitioning is an intended re-run trigger; the focus target is queried fresh each run.
    useEffect(() => {
        if (heroActive)
            document
                .querySelector<HTMLTextAreaElement>(
                    ".bilda-workspace-landing__composer textarea",
                )
                ?.focus();
    }, [heroActive, libraryReady]);

    // Rotate the landing's capability tip on a calm cadence while the landing
    // is on show — paused off-home and when tips are opted out (no background
    // timer). Device-local, like the theme; never touches the event log.
    useEffect(() => {
        if (!heroActive || tipsDismissed) return;
        const id = window.setInterval(
            () => setTipIndex((i) => i + 1),
            TIP_ROTATION_MS,
        );
        return () => window.clearInterval(id);
    }, [heroActive, tipsDismissed]);

    // The chat scope, DERIVED (never stored): port capability first, then the
    // pin, then the followed active tab. The ContextSideCard's Scope section
    // names it; there is a single available projection today, so it reads as a
    // plain label (no switch menu).
    const scope: ChatScope =
        ports.conversationScope === "workspace"
            ? { type: "workspace" }
            : anchor.type === "pinned"
              ? { type: "resource", resource: anchor.resource }
              : activeRef !== null
                ? { type: "resource", resource: activeRef }
                : { type: "project" };

    // === Right-panel access (master audit R1) ================================

    // Which slots apply to the ACTIVE view: Review + Grounding project a
    // material (unavailable, with a reason, away from a material tab); Files is
    // conversation-scoped, so always available. Gates the accelerators + palette
    // rows so no entry point summons an inapplicable slot.
    const slotAvailability: Record<SecondarySlot, SlotAvailability> = {
        // The chat itself, docked beside every main view.
        assistant: { type: "available" },
        review:
            activeMaterialId !== null
                ? { type: "available" }
                : {
                      type: "unavailable",
                      reason: t`Review shows proposed changes to a material — open a material first.`,
                  },
        grounding:
            activeMaterialId !== null
                ? { type: "available" }
                : {
                      type: "unavailable",
                      reason: t`Grounding shows a material's quality — open a material first.`,
                  },
        // The chat's panel is conversation-scoped, so always available (its
        // home says so calmly when the chat has nothing to show yet).
        panel: { type: "available" },
    };

    // Summon one right-panel slot — the ONE path the toolbar group, the
    // contextual jumps (issues chip, go-to-change), the palette rows and the
    // ⌥1/⌥2 accelerators all share. Availability gates HERE (D2): the summon
    // group is the single authority, so no entry point — present or future —
    // can summon a slot the toolbar shows as disabled (V3). The arrow's whole
    // effect — parking any active conversation on its neighbour — lives in the
    // frame fold.
    const summonSlot = (slot: SecondarySlot) => {
        if (slotAvailability[slot].type !== "available") return;
        dispatch({ type: "summon-slot", slot });
    };

    // The message channel — the frame fold is the notice's only writer. The
    // shell reads the clock here and stamps every input with it, so the
    // queue's rules stay a pure fold (messageQueue.ts).
    const showNotice = (n: HostNotice) =>
        dispatch({ type: "show-notice", notice: n, at: Date.now() });
    const control = (c: QueueControl) =>
        dispatch({ type: "control-messages", control: c });
    const messageControls: MessageControls = {
        tick: () => control({ type: "tick", at: Date.now() }),
        pause: () => control({ type: "pause", at: Date.now() }),
        resume: () => control({ type: "resume", at: Date.now() }),
        dismiss: (id) => control({ type: "dismiss", id }),
        expand: () => control({ type: "expand" }),
        collapse: () => control({ type: "collapse" }),
    };

    // Float one transient surface over the frame (at most one at a time —
    // the fold swaps, never stacks).
    const openOverlay = (overlay: OverlaySurface) =>
        dispatch({ type: "open-overlay", overlay });

    const closeOverlay = () => dispatch({ type: "close-overlay" });

    // The coaching / guided-tour host — extracted into CoachHost (split-the-hot-
    // file). `view` is the view on screen with a tour (viewTours.ts); the
    // returned overlay renders at the frame root below, `isFreshForAuthor` +
    // `requestGuidedTour` relay into HeroHost, and `resetCoaching` backs the
    // dev reset-onboarding command. Placed between the tip-rotation effect and
    // the editor host so the coach's measure effect keeps its original
    // registration order.
    const coach = useCoachHost({
        view: tourViewOf(
            tabs.active,
            frame.studioSurface === "pages" &&
                studioOpenOf(
                    frame.studioProject,
                    library === null ? null : (library.projects ?? []),
                ).type === "open",
        ),
        openView: (view) => {
            switch (view) {
                case "projects":
                    return dispatch({ type: "open-projects" });
                case "studio":
                    return dispatch({ type: "open-studio" });
                case "materials":
                    return dispatch({ type: "open-search", kind: "material" });
                case "sources":
                    return dispatch({ type: "open-search", kind: "source" });
                default:
                    return exhaustSwitchCase(view);
            }
        },
        showNotice,
        library,
        conversationSummaries,
        tipsDismissed,
    });

    // Re-evaluate the active material's quality after content changed, so the
    // Grounding pane + toolbar issues badge reflect the edit (a manual text/
    // feedback change moves LIX and can move source overlap). Both evaluate
    // commands are pure server computations (no LLM/key), so this is cheap.
    // Only refreshes an ALREADY-loaded read — an untouched (null) quality stays
    // deferred until the pane is first opened. EditorHost (strangler-fig P3)
    // calls this instead of holding a quality/policy setter itself, so quality
    // ownership can move to a future QualityHost without EditorHost changing.
    const onEditorQualityStale = () => {
        if (activeMaterialId === null || quality === null) return;
        const run = async () => {
            const [q, p] = await Promise.all([
                ports.evaluateMaterial(activeMaterialId),
                ports.evaluateMaterialPolicy(
                    activeMaterialId,
                    Core.Policy.Boundary.write(),
                ),
            ]);
            if (Result.isOk(q)) setQuality(q.value);
            if (Result.isOk(p)) setPolicy(p.value);
        };
        void run();
    };

    // The material-content editing surface's state + handlers (strangler-fig
    // P3 — .claude/analysis/ui-ddd-decomposition.md §6 Phase 3): every callback
    // it needs is declared above this point, so this is the earliest legal
    // call site — the material-load fan-out effect right below is the first
    // reader of `editor.resetForMaterialSwitch`/`applyLoadedMaterial`/etc.
    // The Studio's commands (containers, parts, the desk, Create).
    const studio = useStudioCommands({
        ports,
        library,
        setLibrary,
        showNotice,
        displayText,
        retitle: (id, title) =>
            dispatch({ type: "retitle-resource", id, title }),
        place,
        project: frame.studioProject,
    });
    // On the Studio's pages, "/" at the caret is the one Create menu (spec
    // §5): Ask here, Create from a source…, the headings, and under Insert
    // the parts and a question. Ask here is written in the line (pen Q66).
    const asking = studio.create.asking;
    const surfaceSlash: SurfaceSlash | undefined =
        tabs.active.type === "studio" && frame.studioSurface === "pages"
            ? {
                  items: studioSlashItems(i18n),
                  keep: "headings",
                  onPick: ({ at, anchor, words, how }) => {
                      const key = studioSlashKeyAt(at);
                      // On a course or module page a part lands where "/"
                      // was typed, after the parts above it.
                      const onContainer =
                          place.edited !== undefined &&
                          place.desk.path.at(-1)?.kind !== "document";
                      if (key !== undefined)
                          studio.create.slash(
                              key,
                              anchor,
                              words,
                              how === "enter",
                              onContainer
                                  ? partsBefore(editor.elements ?? [], anchor)
                                  : undefined,
                          );
                  },
                  heldAt: studio.create.caret,
                  asking:
                      asking?.type === "in_text"
                          ? { at: asking.anchor, words: asking.words }
                          : undefined,
                  onAsk: studio.create.askSend,
                  onLeaveAsk: studio.create.askLeave,
                  // The one create command's ceiling on a request.
                  askMax: Core.Limits.LONG_TEXT_MAX,
              }
            : undefined;

    const editor = useEditorHost({
        ports,
        activeMaterialId,
        dispatch,
        onQualityStale: onEditorQualityStale,
        showNotice,
        closeOverlay,
        surfaceSlash,
    });

    return {
        ports,
        studio,
        displayText,
        turnFailureText,
        kbdPlatform,
        // F48 — the 404 gate fact + its one way out, read by AppShellHost.
        routeNotFound,
        onRouteHome,
        frame,
        dispatch,
        overlay,
        paneTree,
        ...transientSurfaces,
        displayName,
        panelLayout,
        resizeRail,
        resizeDock,
        resetRail,
        resetDock,
        dockDragPull,
        setDockDragPull,
        railDragPull,
        setRailDragPull,
        dockDragCfg,
        railDragCfg,
        endDockDrag,
        endRailDrag,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange,
        library,
        setLibrary,
        libraryError,
        setLibraryError,
        authStatus,
        setAuthStatus,
        bootHealthSigRef,
        paneIdSeqRef,
        loadedMaterialRef,
        sourceDetail,
        setSourceDetail,
        sourceError,
        setSourceError,
        sourceReloadKey,
        setSourceReloadKey,
        exportDetail,
        setExportDetail,
        exportError,
        setExportError,
        chatState,
        sendChat,
        chatRuntime,
        selectedConversationId,
        sessionFor,
        conversationList,
        setConversationList,
        conversationListSeq,
        retryConversationList,
        conversationSummaries,
        updateConversationSummaries,
        noteConversationStarted,
        conversationIsDurable,
        conversationTranscriptPending,
        learningContexts,
        setLearningContexts,
        quality,
        setQuality,
        policy,
        setPolicy,
        policyCache,
        setPolicyCache,
        paneSubject,
        setPaneSubject,
        pendingProposalsByMaterialRef,
        tipIndex,
        tipsDismissed,
        setTipsDismissed,
        ...authoringDraft,
        ingest,
        setIngest,
        ingestConversations,
        setIngestConversations,
        ingestHostRef,
        openFilePicker,
        addPastedText,
        materialRefs,
        sourceItems,
        refFor,
        resolveAmbientItems,
        composerCandidates,
        chatSlashCommands,
        navHost,
        revealRail,
        isSplit,
        tabs,
        activeRef,
        activeMaterialRef,
        activeMaterialId,
        activeConversationId,
        visibleConversationIds,
        visibleConversationIdsKey,
        drafting,
        activeSourceId,
        activeExportId,
        activeQualityMaterialId,
        activeContextAnchor,
        activeContextAnchorKey,
        dock,
        surface,
        scope,
        slotAvailability,
        summonSlot,
        showNotice,
        messageControls,
        openOverlay,
        closeOverlay,
        coach,
        editor,
    };
};
